import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import Swal from 'sweetalert2';
import { TarefasEmpresaService } from 'src/app/empresas/services/tarefas-empresa.service';
import { HomeService } from 'src/app/home/home-service/home.service';
import { TarefaEmpresaRecord } from 'src/app/empresas/interfaces/tarefas-empresa.interface';

@Component({
  selector: 'app-editar-tarefa-empresa',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './editar-tarefa-empresa.component.html',
  styleUrl: './editar-tarefa-empresa.component.scss',
})
export class EditarTarefaEmpresaComponent implements OnInit {
  @Input() tarefa!: TarefaEmpresaRecord;

  @Output() fechar = new EventEmitter<void>();
  @Output() tarefaEditada = new EventEmitter<void>();

  responsibleID = '';
  actionID = '';
  data = '';
  hora = '';
  description = '';
  erro = false;
  carregando = false;

  responsaveis: any[] = [];
  acoes: any[] = [];

  private readonly _tarefasService = inject(TarefasEmpresaService);
  private readonly _homeService = inject(HomeService);

  ngOnInit(): void {
    this.popularFormulario();
    this.popularResponsaveis();
    this.popularAcoes();
  }

  get empresaNome(): string {
    return this.tarefa?.companyTradeName || this.tarefa?.companyName || '';
  }

  get contatoNome(): string {
    return this.tarefa?.contactName ?? '';
  }

  private popularFormulario(): void {
    if (!this.tarefa) return;

    this.responsibleID = this.tarefa.responsibleId ? String(this.tarefa.responsibleId) : '';
    this.actionID = this.tarefa.actionId ? String(this.tarefa.actionId) : '';
    this.description = this.tarefa.description ?? '';

    const { data, hora } = this.separarFollowUp(
      this.tarefa.followUp || this.tarefa.followUpFormat,
    );
    this.data = data;
    this.hora = hora;
  }

  private separarFollowUp(followUp: string | null | undefined): { data: string; hora: string } {
    if (!followUp) return { data: '', hora: '' };

    const isoMatch = followUp.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (isoMatch) {
      const [, ano, mes, dia, hh, mm] = isoMatch;
      return { data: `${ano}-${mes}-${dia}`, hora: `${hh}:${mm}` };
    }

    const brMatch = followUp.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ ,]+(\d{2}):(\d{2}))?/);
    if (brMatch) {
      const [, dia, mes, ano, hh, mm] = brMatch;
      return { data: `${ano}-${mes}-${dia}`, hora: hh && mm ? `${hh}:${mm}` : '' };
    }

    return { data: '', hora: '' };
  }

  popularResponsaveis(): void {
    this._homeService.listResponsables().subscribe({
      next: (res: any) => {
        this.responsaveis = (res?.data ?? []).sort((a: any, b: any) => a.name.localeCompare(b.name));
      },
      error: () => {},
    });
  }

  popularAcoes(): void {
    this._tarefasService.listarAcoes().subscribe({
      next: (res: any) => {
        this.acoes = res?.data ?? [];
      },
      error: () => {},
    });
  }

  formatFollowUp(): string {
    if (!this.data || !this.hora) return '';
    const horaFormatada = this.hora.length === 5 ? `${this.hora}:00` : this.hora;
    return `${this.data}T${horaFormatada}`;
  }

  editar(): void {
    this.erro = false;

    if (!this.tarefa?.taskId || !this.responsibleID || !this.actionID || !this.data || !this.hora) {
      this.erro = true;
      return;
    }

    const body = {
      taskID: this.tarefa.taskId,
      responsibleID: Number(this.responsibleID),
      actionID: Number(this.actionID),
      followUp: this.formatFollowUp(),
      description: this.description,
    };

    this.carregando = true;
    this._tarefasService.editarTarefa(body).subscribe({
      next: () => {
        this.carregando = false;
        Swal.fire('Sucesso!', 'Tarefa editada com sucesso!', 'success').then(() => {
          this.tarefaEditada.emit();
          this.fechar.emit();
        });
      },
      error: (err) => {
        this.carregando = false;
        Swal.fire('Erro', err?.error?.message ?? 'Não foi possível editar a tarefa.', 'error');
      },
    });
  }
}
